import { AxiosResponse } from 'axios';
import { saveAs } from 'file-saver';

/**
 * Per-request timeout for the report-card PDF, overriding the shared axios
 * instance's 10s default.
 *
 * The PDF is rendered server-side with puppeteer: a cold Chromium launch plus
 * waiting on the remote institute logo and student photo routinely takes longer
 * than 10s on the first request of a deploy, which surfaced as
 * "timeout of 10000ms exceeded" while the PDF was still being produced.
 */
export const REPORT_CARD_PDF_TIMEOUT_MS = 60_000;

/**
 * Saves a report-card PDF response to disk. Shared by the staff and parent
 * report-card pages so both produce the same filename.
 *
 * The server already sends a sensible name on Content-Disposition
 * (`report_card_<student>_<exam>.pdf`); we prefer it and only fall back to a
 * locally composed name when the header is stripped (some proxies drop it and
 * it is not exposed cross-origin unless allowlisted).
 */
export const saveReportCardPdf = (response: AxiosResponse<Blob>, fallbackName: string): void => {
  saveAs(response.data, readFilename(response) || fallbackName);
};

/** `attachment; filename="report_card_Kid_One_Mid_Term.pdf"` → the filename. */
const readFilename = (response: AxiosResponse<Blob>): string | null => {
  const disposition =
    response.headers?.['content-disposition'] ?? response.headers?.['Content-Disposition'];
  if (typeof disposition !== 'string') return null;

  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  if (!match?.[1]) return null;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    // A malformed percent-escape must not cost the user their download.
    return match[1];
  }
};

/**
 * Locally composed fallback filename, matching the server's convention:
 * `report_card_<student>_<exam>.pdf` with non-alphanumerics collapsed.
 */
export const buildReportCardFileName = (studentName: string, examTitle: string): string => {
  const slug = (value: string): string =>
    value
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'report_card';

  return `report_card_${slug(studentName)}_${slug(examTitle)}.pdf`;
};

/**
 * On a failed `responseType: 'blob'` request the server's JSON error arrives as
 * a Blob, and the api.tsx interceptor rejects with that response BODY — so the
 * caught value IS the Blob, not an AxiosError wrapping it. Read the JSON out of
 * it to surface the real reason (e.g. "this exam has not been completed")
 * instead of a generic failure.
 */
export const readBlobErrorMessage = async (
  error: unknown,
  fallbackMessage: string,
): Promise<string> => {
  // Cover both shapes: the interceptor's bare body, and a raw AxiosError in
  // case this is ever called from a client without that interceptor.
  const body =
    error instanceof Blob
      ? error
      : (error as { response?: { data?: unknown } })?.response?.data ??
        (error as { data?: unknown })?.data;

  if (body instanceof Blob) {
    try {
      const parsed = JSON.parse(await body.text());
      if (typeof parsed?.message === 'string') return parsed.message;
    } catch {
      // Not JSON — fall through to the generic message.
    }
  }

  // A plain string rejection (the interceptor falls back to error.message).
  if (typeof error === 'string' && error) return error;

  const message = (error as { message?: string })?.message;
  return message || fallbackMessage;
};
